'use client';

import { useEffect, useMemo, useState } from 'react';
import { NodeResort } from '@/utils/drupal-api/__generated__/graphql';
import { ImageSrcSetSize } from '@/utils/image';
import { Resort } from '@/utils/platform-api/__generated__/graphql';
import { Transition, TransitionChild } from '@headlessui/react';
import { BrandConfig } from 'types/brand-config';

import Button from '@/components/ui/button';
import ResortCard from '@/components/resort/resort-card';

const timer = 5000;

const cardImageSizes: ImageSrcSetSize[] = [
  { viewport: 320, image: 480 },
  { viewport: 1023, image: 640 },
  { viewport: 1600, image: 480 },
];

export default function ResortsCarousel({
  resorts,
  brand,
  cmsResorts,
}: {
  resorts: Resort[];
  brand: BrandConfig;
  cmsResorts?: NodeResort[];
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  // Automatically transition slides every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === resorts.length - 1 ? 0 : prevIndex + 1
      );
    }, timer);

    return () => clearInterval(interval);
  }, [resorts.length]);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === resorts.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? resorts.length - 1 : prevIndex - 1
    );
  };

  const elements = useMemo(
    () =>
      resorts.map((resort, index) => {
        const image = resort.images?.find((image) => image.url);
        const cmsResort = cmsResorts?.find(
          (cmsResort) => cmsResort.externalId === resort.id
        );
        if (!image || !cmsResort) {
          return null;
        }

        return (
          <Transition
            as="div"
            key={resort.id}
            show={index === currentIndex}
            enter="transition-opacity duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <TransitionChild as="div" className="min-h-[300px]">
              <ResortCard
                key={resort.id}
                resort={resort as Resort}
                cmsResort={cmsResort}
                className="aspect-auto h-[300px] max-w-screen-2xl rounded-lg"
                imageSizes={cardImageSizes}
                imageAspect={2 / 1}
                brand={brand}
              />
            </TransitionChild>
          </Transition>
        );
      }),
    [resorts, currentIndex]
  );

  return (
    <div className="relative w-full overflow-hidden">
      {/* Navigation buttons wrapping elements */}
      <div className="flex flex-row items-center justify-evenly gap-4">
        <Button
          onClick={prevSlide}
          className="hidden rounded-full bg-white p-2 shadow-lg md:block"
        >
          ❮
        </Button>
        <div className="relative flex min-h-[300px] w-full justify-center overflow-hidden">
          {elements}
        </div>
        <Button
          onClick={nextSlide}
          className="hidden rounded-full bg-white p-2 shadow-lg md:block"
        >
          ❯
        </Button>
      </div>
      {/* Dots indicator */}
      <div className="mt-[1rem] flex justify-center space-x-2">
        {resorts.map((_, index) => (
          <Button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`h-3 w-3 rounded-full p-0 ${
              index === currentIndex ? 'bg-primary-main' : '!bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
